
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { DeviceInfo, DeviceType } from '@/types/device-info';
import { State, Action } from 'vuex-class';
import api from '@/modules/api';
import { PricingResolved } from '@/types/pricing-plan';
import { PLAN_TYPE_PREMIUM, PLAN_TYPE_PRODUCT } from '@/utils/constants';
import PlanCard from '@/components/devices/plans/PlanCard.vue';
import DeviceQuickActions from '@/components/quick-actions/DeviceQuickActions.vue';
import { mutations, actions } from '@/store/types';
import { UserInfo } from '@/types/user-info';
import {
    PauseUserSubscriptionActionType,
    ResumeUserSubscriptionActionType,
    CancelUserSubscriptionActionType,
    UpdateDeviceActionType,
    UpdateUserActionType,
} from '@/types/store/actions';
import { Subscription } from '@/types/subscriptions';
import DeviceCurrentSubscription from '@/components/devices/plans/deviceCurrentSubscription.vue';
import { getDeviceSubscription, planUpdateAble, getSchedulePlanId } from '@/utils/subscriptions';
import * as dlg from '@/modules/dlg';
import PlanDetailView from '../plans/PlanDetailView.vue';
import PaymentCardModal from '@/components/devices/popups/PaymentCardModal.vue';
import UserCountryModal from '@/components/users/popups/UserCountryModal.vue';
import JoinGroupModal from '@/components/devices/popups/JoinGroupModal.vue';
import { PaymentUser } from '@/types/payment';
import { centsToUsd } from '@/utils/amount';
import { getErrorMessage } from '@/utils/common';
import { validatePrivilege } from '@/utils/devices';
import { DevicePrivileges } from '@/constants/device_privileges';
import { defaultCurrency } from '@/constants/currency';
import { resolveUserCountry } from '@/utils/countries';
import { getCurrencySymbol } from '@/utils/currency';

@Component({
    components: {
        PlanCard,
        DeviceQuickActions,
        DeviceCurrentSubscription,
        PlanDetailView,
        PaymentCardModal,
        UserCountryModal,
        JoinGroupModal,
    },
})
export default class DeviceTransferPlansSettings extends Vue {
    @Action(actions.UPDATE_USER)
    updateUser!: UpdateUserActionType;

    @Action(actions.UPDATE_DEVICE)
    updateDevice!: UpdateDeviceActionType;

    @Action(actions.PAUSE_USER_SUBSCRIPTION)
    pauseUserSubscription!: PauseUserSubscriptionActionType;

    @Action(actions.RESUME_USER_SUBSCRIPTION)
    resumeUserSubscription!: ResumeUserSubscriptionActionType;

    @Action(actions.CANCEL_USER_SUBSCRIPTION)
    cancelUserSubscription!: CancelUserSubscriptionActionType;

    plan: PricingResolved | null = null;
    plansLoaded = false;
    subscribedPlans: Subscription[] = [];
    tableLoading = false;
    panel: number[] = [];
    isUpdateAble = false;
    schedulePlanId = '';
    planTotal = '';
    stripePaymentUser: PaymentUser | null = null;
    planToPurchase: PricingResolved | null = null;
    paymentMethods: any[] = [];
    paymentCardModal = false;
    countryModal = false;
    joinGroupModal = false;
    protected plans: PricingResolved[] = [];
    protected DevicePrivileges = DevicePrivileges;
    protected validatePrivilege = validatePrivilege;
    userSubscriptions: Subscription[] = [];

    swiperOption() {
        return {
            breakpoints: {
                // when window width is >= 320px
                320: {
                    slidesPerView: 1,
                },
                590: {
                    slidesPerView: 2,
                },
                730: {
                    slidesPerView: 2,
                    spaceBetween: 20,
                },
                767: {
                    slidesPerView: 1,
                    spaceBetween: 20,
                },
                870: {
                    slidesPerView: 2,
                    spaceBetween: 20,
                },
                1000: {
                    slidesPerView: 2,
                },
                1200: {
                    slidesPerView: 3,
                },
                1650: {
                    slidesPerView: 4,
                },
                2100: {
                    slidesPerView: 5,
                },
            },
            spaceBetween: 40,
            pagination: {
                el: '.swiper-pagination',
                clickable: true,
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
        };
    }

    @Prop({ required: true })
    device!: DeviceInfo;

    @Prop({ required: true })
    feeChagre!: number;

    @Prop({ required: true })
    user!: UserInfo;

    @State('devices')
    devices!: DeviceInfo[];

    centsToUsd = centsToUsd;

    async updateDeviceTrial() {
        await this.updateDevice({
            device: {
                _id: this.device._id,
                isTrialAvailed: true,
            },
        });
    }

    async refresh() {
        try {
            // load active plans
            this.plans = [];
            const { data } = await api.loadResolvedPlans({ userId: this.user._id }, this.device._id as string);
            if (this.device.isTrialAvailed === true && data.length > 0) {
                for (let i in data) {
                    data[i].freeTrialDays = 0;
                }
            }
            this.plans = data;

            // load subscribed plan
            this.subscribedPlans = getDeviceSubscription(this.userSubscriptions, this.device.deviceUUID);
            if (this.subscribedPlans.length > 0) {
                this.isUpdateAble = planUpdateAble(this.subscribedPlans[0]);
                this.schedulePlanId = getSchedulePlanId(this.subscribedPlans[0]);
            } else {
                this.isUpdateAble = true;
                this.schedulePlanId = '';
            }
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.plansLoaded = true;
        }
    }

    async mounted() {
        this.userSubscriptions = await api.loadUserSubscriptions(this.user._id ?? '');
        if (this.user.country) this.loadPage();
        else this.validateCountry();
    }

    async loadPage() {
        this.getPaymentCustomer();
        this.refresh();
    }

    selected(plan: PricingResolved) {
        return !!(this.plan && this.plan._id === plan.planId);
    }

    async validateCountry() {
        if (!this.user.country) {
            const countryDetails = await resolveUserCountry();
            if (countryDetails) {
                await this.updateUserCountry(countryDetails);
                this.loadPage();
            } else {
                this.countryModal = true;
            }
        }
    }

    async updateUserCountry(countryDetails: any) {
        const { updatedUser } = await api.updateUser({
            country: countryDetails.value,
            currency: countryDetails.isoCode ? countryDetails.isoCode : defaultCurrency,
        });
        this.$store.commit(mutations.SET_USER, { user: updatedUser });
        if (this.countryModal) {
            this.countryModal = false;
            this.loadPage();
        }
    }

    // stripe
    async getPaymentCustomer() {
        try {
            const paymentUser = await api.getPaymentUser(this.user._id);
            this.stripePaymentUser = paymentUser.paymentUser ?? null;
            this.paymentMethods = paymentUser.paymentMethods ?? [];
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        }
    }
    validatePaymentMethod(plan: PricingResolved) {
        this.paymentCardModal = true;
        this.planToPurchase = plan;
        const currency = getCurrencySymbol(plan?.currency ? plan?.currency : 'USD');
        let amount = plan?.price as number;
        if (plan?.taxRate) {
            let taxAmount = (amount / 100) * plan?.taxRate.percentage;
            amount += taxAmount;
        }
        if (this.feeChagre) amount += Number(this.feeChagre);
        this.planTotal = `${currency}${amount.toFixed(2)}`;
    }
    async onPaymentDetailSubmit(couponCode: string) {
        this.paymentCardModal = false;

        // refresh payment user
        await this.getPaymentCustomer();

        await this.handlePurchase(this.planToPurchase as PricingResolved, couponCode);
    }
    async onPaymentMethodRemove(cardId: string) {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.removePaymentMethod(cardId);
            // refresh payment user
            await this.getPaymentCustomer();
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }
    // stripe

    async handlePurchase(plan: PricingResolved, couponCode: string) {
        const oldSubscriptionId = this.subscribedPlans.length > 0 ? this.subscribedPlans[0]._id : null;
        let isUpgrade = false;
        let paymentMethod = 'card';
        if (oldSubscriptionId) {
            isUpgrade = plan.price > parseInt(centsToUsd(this.subscribedPlans[0].sourceData.plan.amount));
            const alertMessage = `Are you sure you want to change your plan to "${plan.name}"?`;
            const confirmed = await dlg.confirm(alertMessage, 'Change plan');
            if (!confirmed) return;
        }
        if (this.paymentMethods[0]?.card?.wallet) {
            paymentMethod = this.paymentMethods[0].card.wallet.type;
        }
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.k9SubscribeUser(
                this.user,
                plan._id as string,
                this.device.deviceUUID,
                oldSubscriptionId as string,
                isUpgrade as boolean,
                couponCode,
                paymentMethod,
                this.feeChagre,
            );

            // update device trial
            if (plan.freeTrialDays > 0 && this.device.isTrialAvailed !== true) {
                await this.updateDeviceTrial();
            }

            // refresh plan cards and subscribed plan list
            await this.refresh();

            // refresh payment user
            await this.getPaymentCustomer();

            const response = await api.transferDevice(this.device._id as string, this.user._id as string);

            const filteredDevices = this.devices.filter((device) => device._id !== this.device._id);

            this.$store.commit(mutations.SET_DEVICES, { devices: filteredDevices });

            this.$emit('updateValueSelected', false);

            Vue.toasted.success(response.message);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    get showProductsPlans() {
        return this.device && [DeviceType.AirboltGps].includes(this.device.deviceType);
    }

    get showPremiumPlans() {
        return true;
    }

    get productPlans() {
        const filteredList = this.plans.filter(
            (plan) => plan.type === PLAN_TYPE_PRODUCT && plan.product?.type === this.device.deviceType,
        );
        return this.sortPopulatePlan(filteredList);
    }

    get premiumPlans() {
        const filteredList = this.plans.filter((plan) => plan.type === PLAN_TYPE_PREMIUM);
        return this.sortPopulatePlan(filteredList);
    }

    sortPopulatePlan(plansList: PricingResolved[]) {
        const index = plansList.findIndex((x) => x.mostPopularPlan === true);
        if (index > -1) {
            let placementIndex = 0;
            let cards = 1;
            if (
                window.matchMedia('(min-width:590px) and (max-width: 767px)').matches ||
                window.matchMedia('(min-width:870px) and (max-width: 1200px)').matches
            ) {
                cards = 2;
                placementIndex = 1;
            }

            if (window.matchMedia('(min-width:1200px) and (max-width: 1650px)').matches) {
                cards = 3;
                placementIndex = 1;
            }

            if (window.matchMedia('(min-width:1650px) and (max-width: 2100px)').matches) {
                cards = 4;
                placementIndex = 1;
            }

            if (window.matchMedia('(min-width:2100px)').matches) {
                cards = 5;
                placementIndex = 2;
            }

            if (cards > plansList.length) placementIndex = Math.floor(plansList.length / 2);

            if (plansList[placementIndex])
                [plansList[placementIndex], plansList[index]] = [plansList[index], plansList[placementIndex]];
        }
        return plansList;
    }

    onViewDetails(plan: PricingResolved) {
        this.plan = plan;
        window.scrollTo(0, 0);
        this.panel = [0];
    }

    @Watch('device', { immediate: true, deep: true })
    onDeviceChanged(device: DeviceInfo) {
        if (device) {
            // Do something later
        }
    }
}
